import { Breadcrumbs, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Button, ConfirmActionModal } from '@xver/components/Atoms';
import { useDeleteBaremetalMutate } from '@xver/hooks/CloudComputeHooks/useDeleteBaremetalMutate';
import { useAlignActiveProject } from '@xver/hooks/useActiveProject';
import { focusRowMenu } from '@xver/hooks/useMoveResource';
import useBreadcrumbItems from '@xver/hooks/useBreadcrumbItems';
import usePageHeading from '@xver/hooks/usePageHeading';
import { imageOsLabel, resolveImageLabel } from '@xver/utils/baremetal';
import { getErrorMessage, getStatusCode } from '@xver/utils/error';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useBaremetalLaunchOptionQuery } from '../BareMetalComputeDeploymentPage/hooks/useBaremetalLaunchOptionQuery';
import BaremetalHeader from './components/BaremetalHeader';
import BillingCard from './components/BillingCard';
import CredentialsCard from './components/CredentialsCard';
import DangerZoneCard from './components/DangerZoneCard';
import SystemInfoCard from './components/SystemInfoCard';
import { useBaremetalQuery } from './hooks/useBaremetalQuery';

const LIST_PATH = '/bare-metal-compute';

export default function BareMetalComputeInstancePage() {
    const { t } = useTranslation('bareMetal');
    const navigate = useNavigate();
    const { baremetalId } = useParams<'baremetalId'>();

    const { data: baremetal, error, refetch } = useBaremetalQuery(baremetalId);
    useAlignActiveProject(baremetal?.project_id);
    const isUnavailable = !baremetal && !!error && getStatusCode(error) !== 404;
    const headingRef = usePageHeading(
        baremetal?.hostname ?? (isUnavailable ? t('detail.unavailableTitle') : undefined),
        t('breadcrumb.bareMetal')
    );
    const { data: launchOptions } = useBaremetalLaunchOptionQuery();
    const deleteMutation = useDeleteBaremetalMutate();
    const [destroyOpened, { open: openDestroy, close: closeDestroy }] = useDisclosure(false);
    const destroyFromMenuRef = useRef(false);

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.product'), href: '#' },
        { title: t('breadcrumb.bareMetal'), href: LIST_PATH },
        { title: baremetal?.hostname ?? `#${baremetalId ?? '...'}` },
    ]);

    if (!baremetal) {
        if (isUnavailable) {
            return (
                <div className="w-full min-h-full flex flex-col justify-start items-start gap-6">
                    <Breadcrumbs separatorMargin="xs">{breadcrumbItems}</Breadcrumbs>
                    <div
                        role="alert"
                        className="w-full max-w-xl mx-auto mt-12 p-6 rounded-md bg-surface-1 border-[0.5px] border-line flex flex-col items-center text-center gap-3"
                    >
                        <IconAlertTriangle size={32} color="#f59e0b" aria-hidden />
                        <h1 ref={headingRef} tabIndex={-1} className="text-xl font-bold text-fg">
                            {t('detail.unavailableTitle')}
                        </h1>
                        <p className="text-sm text-fg/68">{getErrorMessage(error)}</p>
                        <div className="flex flex-row flex-wrap justify-center gap-2 mt-2">
                            <Button variant="secondary" onClick={() => navigate(LIST_PATH)}>
                                {t('detail.backToList')}
                            </Button>
                            <Button onClick={() => void refetch()}>{t('detail.retry')}</Button>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="w-full h-full flex flex-col items-center justify-center">
                <Loader size="lg" type="bars" />
            </div>
        );
    }

    const image = resolveImageLabel(
        launchOptions?.images,
        baremetal.image.slug,
        baremetal.image.version_slug
    );
    const username = baremetal.username || image.defaultUsername;
    const osLabel = imageOsLabel(image);
    const connectTarget = {
        hostname: baremetal.hostname,
        ipAddress: baremetal.network.ip_address,
        username,
        password: baremetal.password,
        isWindows: `${baremetal.image.slug} ${osLabel}`.toLowerCase().includes('windows'),
    };

    const canManage = baremetal.access !== 'view';

    const openDestroyFrom = (isFromMenu: boolean) => () => {
        destroyFromMenuRef.current = isFromMenu;
        openDestroy();
    };

    const cancelDestroy = () => {
        if (destroyFromMenuRef.current) focusRowMenu('baremetal', baremetal.id);
        closeDestroy();
    };

    const confirmDestroy = () =>
        deleteMutation.mutate(baremetal.id, {
            onSuccess: () => {
                closeDestroy();
                navigate(LIST_PATH);
            },
        });

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-start gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin="xs" className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <BaremetalHeader
                    baremetal={baremetal}
                    osLabel={osLabel}
                    connectTarget={connectTarget}
                    canManage={canManage}
                    isDestroying={deleteMutation.isPending}
                    onRefresh={() => void refetch()}
                    onDestroy={openDestroyFrom(true)}
                    headingRef={headingRef}
                />
            </div>

            <div className="w-full grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-4 items-start">
                <div className="flex flex-col gap-4 min-w-0">
                    <SystemInfoCard baremetal={baremetal} image={image} />
                    <CredentialsCard
                        username={username}
                        password={baremetal.password}
                        ipAddress={baremetal.network.ip_address}
                        isWindows={connectTarget.isWindows}
                    />
                </div>
                <div className="flex flex-col gap-4 min-w-0">
                    <BillingCard baremetal={baremetal} canManage={canManage} />
                    {canManage && (
                        <DangerZoneCard
                            isDestroying={deleteMutation.isPending}
                            onDestroy={openDestroyFrom(false)}
                        />
                    )}
                </div>
            </div>

            <ConfirmActionModal
                opened={destroyOpened}
                onClose={cancelDestroy}
                title={t('destroyModal.title')}
                message={t('destroyModal.message', { name: baremetal.hostname })}
                confirmLabel={t('destroyModal.destroy')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDestroy}
            />
        </div>
    );
}
